<template>
  <div class="gc-wo-bulk-update-step-preview">
    <!-- No config state -->
    <div v-if="!materialUpdateConfig" class="pvw-no-config">
      No material update operation has been configured. Return to Configure to
      select an operation before previewing changes.
    </div>

    <!-- Computing -->
    <div v-else-if="computing" class="pvw-computing">
      <div class="pvw-computing-inner">
        <div class="pvw-spinner"></div>
        <span>Computing changes...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="computeError" class="pvw-error-state">
      <span class="pvw-error-msg">{{ computeError }}</span>
      <DxButton
        text="Retry"
        type="normal"
        styling-mode="outlined"
        @click="computeDiff"
      />
    </div>

    <!-- Content -->
    <div v-else-if="diffWos.length > 0" class="pvw-layout">
      <!-- Main column -->
      <div class="pvw-main">
        <div class="pvw-card">
          <div class="pvw-card-header">
            <span class="pvw-card-title">Change preview — per work order</span>
          </div>
          <div class="pvw-card-body">
            <!-- All-NA banner -->
            <div v-if="allLinesNotApplicable" class="pvw-all-na-banner">
              No material lines can be updated — all included lines have actual
              quantities or validation conflicts.
            </div>

            <!-- Filter pills -->
            <div class="pvw-fpills">
              <div
                v-for="pill in filterPills"
                :key="pill.key"
                :class="['pvw-fpill', { active: activeFilter === pill.key }]"
                @click="activeFilter = pill.key"
              >
                {{ pill.label }} ({{ pill.count }})
              </div>
            </div>

            <!-- Colour legend -->
            <div class="pvw-legend">
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-green"></span>Material replaced
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-amber"></span>Qty changed
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-grey"></span>No change
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-red"></span>Not applicable
              </div>
            </div>

            <!-- WO diff panels -->
            <div class="pvw-diff-list">
              <GcWoBulkUpdateWoDiffPanel
                v-for="wo in filteredWos"
                :key="wo.wono"
                :wo="wo"
                :is-excluded="isExcluded(wo.wono)"
                :is-read-only="isReadOnly"
                @exclude="onExcludeWo"
                @include="onIncludeWo"
              />
              <div v-if="filteredWos.length === 0" class="pvw-empty">
                No work orders match this filter.
              </div>
            </div>

            <div class="pvw-count-footer">
              Showing {{ filteredWos.length }} of {{ diffWos.length }} work
              orders
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar — Preview summary -->
      <div class="pvw-sidebar">
        <div class="pvw-sum-card">
          <div class="pvw-sum-header">
            <span class="pvw-sum-title">Preview summary</span>
          </div>
          <div class="pvw-sum-body">
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">WOs included</span>
              <span class="pvw-sum-val pvw-sv-blue">{{
                summaryStats.includedWos
              }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">WOs excluded</span>
              <span
                :class="[
                  'pvw-sum-val',
                  summaryStats.excludedWos > 0 ? 'pvw-sv-red' : '',
                ]"
                >{{ summaryStats.excludedWos }}</span
              >
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines replaced</span>
              <span
                :class="['pvw-sum-val', summaryStats.linesReplaced > 0 ? 'pvw-sv-green' : '']"
              >{{ summaryStats.linesReplaced }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines unchanged</span>
              <span class="pvw-sum-val">{{ summaryStats.linesUnchanged }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines added</span>
              <span class="pvw-sum-val">{{ summaryStats.linesAdded }}</span>
            </div>
            <div v-if="summaryStats.linesNotApplicable > 0" class="pvw-sum-row">
              <span class="pvw-sum-lbl">Not applicable</span>
              <span class="pvw-sum-val pvw-sv-red">{{ summaryStats.linesNotApplicable }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state after compute returned no lines -->
    <div v-else-if="diffInitialized" class="pvw-empty-state">
      No material lines were returned for the selected work orders.
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import { WoBulkUpdateService } from "@/_services/api/production/wo-bulk-update.service";
import GcWoBulkUpdateWoDiffPanel from "./gc-wo-bulk-update-wo-diff-panel.vue";

export default {
  name: "GcWoBulkUpdateStepPreview",
  components: { DxButton, GcWoBulkUpdateWoDiffPanel },
  props: {
    active: { type: Boolean, default: false },
    matchedWoList: { type: Array, default: function() { return []; } },
    excludedWoIds: { type: Array, default: function() { return []; } },
    materialUpdateConfig: { type: Object, default: null },
    companyCode: { type: String, default: null },
    transactionId: { type: Number, default: null },
    diffSnapshot: { type: Object, default: null },
    isReadOnly: { type: Boolean, default: false },
    searchCriteria: { type: Object, default: null },
  },
  data() {
    return {
      computing: false,
      computeError: null,
      diffWos: [],
      diffInitialized: false,
      activeFilter: "all",
    };
  },
  computed: {
    allLinesNotApplicable() {
      var excluded = this.excludedWoIds;
      var included = this.diffWos.filter(function(wo) {
        return excluded.indexOf(wo.wono) === -1;
      });
      if (!included.length) return false;
      return included.every(function(wo) {
        return wo.lines.every(function(l) {
          return !l.canApply || l.changeType === 'NoChange';
        });
      });
    },
    filterPills() {
      var all = this.diffWos;
      var excluded = this.excludedWoIds;
      function isExcl(wo) { return excluded.indexOf(wo.wono) !== -1; }
      function hasChange(wo) {
        return wo.lines.some(function(l) {
          return l.canApply && l.changeType !== 'NoChange';
        });
      }
      function isNoChange(wo) {
        return !isExcl(wo) && wo.lines.every(function(l) {
          return !l.canApply || l.changeType === 'NoChange';
        });
      }
      var changesWos = all.filter(function(wo) { return !isExcl(wo) && hasChange(wo); });
      var anyReplaced = changesWos.some(function(wo) {
        return wo.lines.some(function(l) {
          return l.changeType === 'MaterialReplaced' || l.changeType === 'MaterialAndQtyReplaced';
        });
      });
      var changesLabel = anyReplaced ? 'Replacements' : 'Qty changes';
      return [
        { key: 'all', label: 'All', count: all.length },
        { key: 'changes', label: changesLabel, count: changesWos.length },
        { key: 'nochange', label: 'No changes', count: all.filter(isNoChange).length },
        { key: 'excluded', label: 'Excluded', count: all.filter(isExcl).length },
      ];
    },
    filteredWos() {
      var excluded = this.excludedWoIds;
      function isExcl(wo) { return excluded.indexOf(wo.wono) !== -1; }
      switch (this.activeFilter) {
        case 'changes':
          return this.diffWos.filter(function(wo) {
            return !isExcl(wo) && wo.lines.some(function(l) {
              return l.canApply && l.changeType !== 'NoChange';
            });
          });
        case 'nochange':
          return this.diffWos.filter(function(wo) {
            return !isExcl(wo) && wo.lines.every(function(l) {
              return !l.canApply || l.changeType === 'NoChange';
            });
          });
        case 'excluded':
          return this.diffWos.filter(isExcl);
        default:
          return this.diffWos;
      }
    },
    summaryStats() {
      var excluded = this.excludedWoIds;
      var included = this.diffWos.filter(function(wo) {
        return excluded.indexOf(wo.wono) === -1;
      });
      var linesReplaced = 0;
      var linesUnchanged = 0;
      var linesAdded = 0;
      var linesNotApplicable = 0;
      for (var i = 0; i < included.length; i++) {
        var lines = included[i].lines;
        for (var j = 0; j < lines.length; j++) {
          var l = lines[j];
          if (!l.canApply) {
            linesNotApplicable++;
          } else if (l.changeType === 'Added') {
            linesAdded++;
          } else if (l.changeType === 'NoChange') {
            linesUnchanged++;
          } else {
            linesReplaced++;
          }
        }
      }
      return {
        includedWos: included.length,
        excludedWos: this.excludedWoIds.length,
        linesReplaced: linesReplaced,
        linesUnchanged: linesUnchanged,
        linesAdded: linesAdded,
        linesNotApplicable: linesNotApplicable,
      };
    },
  },
  watch: {
    active(val) {
      if (val && !this.diffWos.length && !this.computing) {
        this.initDiff();
      }
    },
  },
  mounted() {
    if (this.active) {
      this.initDiff();
    }
  },
  methods: {
    // ─── Public API ────────────────────────────────────────────────────────────

    reset() {
      this.diffWos = [];
      this.computeError = null;
      this.diffInitialized = false;
      this.activeFilter = "all";
    },

    // ─── Diff lifecycle ────────────────────────────────────────────────────────

    async initDiff() {
      if (this.diffSnapshot && this.diffSnapshot.WoDiffs) {
        this.diffWos = this.normaliseDiff(this.diffSnapshot);
        this.diffInitialized = true;
        this.handleZeroAffectedWos();
      } else {
        await this.computeDiff();
      }
    },
    async computeDiff() {
      if (!this.materialUpdateConfig) return;
      this.computing = true;
      this.computeError = null;
      try {
        var excludedSet = this.excludedWoIds;
        var selectedWoIds = this.matchedWoList
          .map(function(wo) { return wo.Wono; })
          .filter(function(id) { return excludedSet.indexOf(id) === -1; });

        var payload = {
          CompanyCode: this.companyCode,
          SelectedWoIds: selectedWoIds,
          MaterialUpdateConfig: this.materialUpdateConfig,
        };

        // MaterialSearchCriteria — UpdateQtyAll only
        var matConfig = this.materialUpdateConfig;
        var matCriteria = this.searchCriteria;

        if (
          matConfig &&
          matConfig.Operation === "UpdateQtyAll" &&
          matCriteria &&
          matCriteria.MaterialCriteria &&
          matCriteria.MaterialCriteria.length
        ) {
          payload.MaterialSearchCriteria = matCriteria.MaterialCriteria;
        }

        if (this.transactionId) {
          payload.TransactionId = this.transactionId;
        }

        var res = await WoBulkUpdateService.computeDiff(payload);
        this.diffWos = this.normaliseDiff(res.data);
        this.diffInitialized = true;
        this.handleZeroAffectedWos();

        this.$emit("diff-computed", {
          diffSnapshot: res.data,
          diffComputedAt: res.data.DiffComputedAt || new Date().toISOString(),
          allLinesNotApplicable: this.allLinesNotApplicable,
        });
      } catch (e) {
        this.computeError = "Failed to compute changes. Please try again.";
      } finally {
        this.computing = false;
      }
    },

    // ─── Normalisation ─────────────────────────────────────────────────────────

    normaliseDiff(snapshot) {
      if (!snapshot || !snapshot.WoDiffs) return [];
      var result = [];
      var woDiffs = snapshot.WoDiffs || [];
      for (var i = 0; i < woDiffs.length; i++) {
        var wo = woDiffs[i];
        var lines = wo.Lines || [];
        result.push({
          wono: wo.Wono,
          woDescription: wo.WoDescription || '',
          woStatus: wo.WoStatus || '',
          woDate: wo.WoDate || wo.PlannedDate || wo.DueDate || '',
          ProductName: wo.ProductName || '',
          lines: lines.map(function(line) {
            return {
              processCode: line.ProcessCode || '',
              oldMaterialCode: line.OldMaterialCode || '',
              oldDescription: line.OldDescription || '',
              newMaterialCode: line.NewMaterialCode || '',
              newDescription: line.NewDescription || '',
              oldWoQty: line.OldWoQty != null ? line.OldWoQty : null,
              newWoQty: line.NewWoQty != null ? line.NewWoQty : null,
              oldUnitQty: line.OldUnitQty != null ? line.OldUnitQty : null,
              newUnitQty: line.NewUnitQty != null ? line.NewUnitQty : null,
              uom: line.UnitOfMeasure || line.UOM || '',
              canApply: !!line.CanApply,
              blockReason: line.BlockReason || null,
              changeType: line.ChangeType || 'NoChange',
            };
          }),
        });
      }
      return result;
    },

    // ─── Exclude / Include ─────────────────────────────────────────────────────

    isExcluded(wono) {
      return this.excludedWoIds.indexOf(wono) !== -1;
    },
    onExcludeWo(wono) {
      this.$emit("exclude-wo", wono);
    },
    onIncludeWo(wono) {
      this.$emit("include-wo", wono);
    },

    // ─── Auto-exclude WOs with 0 affected lines ────────────────────────────────

    hasAffectedLines(wo) {
      return wo.lines.some(function(l) {
        return l.canApply && l.changeType !== "NoChange";
      });
    },

    handleZeroAffectedWos() {
      var zeroAffectedWos = this.diffWos
        .filter((wo) => !this.hasAffectedLines(wo))
        .map((wo) => wo.wono);

      if (zeroAffectedWos.length > 0) {
        this.$emit("auto-exclude-zero-affected", zeroAffectedWos);
      }
    },
  },
};
</script>

<style lang="scss">
.gc-wo-bulk-update-step-preview {
  // ── No config / empty states ──────────────────────────────────────────────
  .pvw-no-config,
  .pvw-empty-state {
    padding: 32px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 13px;
    color: #6c757d;
    text-align: center;
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  .pvw-computing {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
  }

  .pvw-computing-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #6c757d;
    font-size: 14px;
  }

  .pvw-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #dee2e6;
    border-top-color: #03a9f4;
    border-radius: 50%;
    animation: pvw-spin 0.7s linear infinite;
  }

  @keyframes pvw-spin {
    to {
      transform: rotate(360deg);
    }
  }

  // ── Error ────────────────────────────────────────────────────────────────
  .pvw-error-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 20px;
    background: #fff8f8;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
  }

  .pvw-error-msg {
    font-size: 13px;
    color: #842029;
  }

  // ── All-NA banner ─────────────────────────────────────────────────────────
  .pvw-all-na-banner {
    padding: 10px 16px;
    margin-bottom: 14px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    font-size: 13px;
    color: #856404;
    font-weight: 500;
  }

  // ── Layout ───────────────────────────────────────────────────────────────
  .pvw-layout {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .pvw-main {
    flex: 1;
    min-width: 0;
  }

  .pvw-sidebar {
    width: 240px;
    flex-shrink: 0;
  }

  // ── Main card ────────────────────────────────────────────────────────────
  .pvw-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .pvw-card-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
  }

  .pvw-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
  }

  .pvw-card-body {
    padding: 16px;
  }

  // ── Filter pills ─────────────────────────────────────────────────────────
  .pvw-fpills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
  }

  .pvw-fpill {
    padding: 4px 12px;
    border: 1px solid #dee2e6;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s, border-color 0.12s, color 0.12s;

    &:hover {
      border-color: #03a9f4;
      color: #0369a1;
    }

    &.active {
      background: #03a9f4;
      border-color: #03a9f4;
      color: #fff;
    }
  }

  // ── Legend ───────────────────────────────────────────────────────────────
  .pvw-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    margin-bottom: 16px;
  }

  .pvw-leg-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #495057;
  }

  .pvw-leg-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
  }

  .pvw-dot-green {
    background: #dcfce7;
    border: 1px solid #86efac;
  }

  .pvw-dot-amber {
    background: #fef0c7;
    border: 1px solid #f79009;
  }

  .pvw-dot-grey {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
  }

  .pvw-dot-red {
    background: #fee4e2;
    border: 1px solid #f97066;
  }

  // ── Diff panels list ──────────────────────────────────────────────────────
  .pvw-diff-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  // ── Empty / footer ────────────────────────────────────────────────────────
  .pvw-empty {
    padding: 20px;
    text-align: center;
    color: #adb5bd;
    font-size: 13px;
  }

  .pvw-count-footer {
    margin-top: 12px;
    font-size: 12px;
    color: #6c757d;
  }

  // ── Sidebar summary card ──────────────────────────────────────────────────
  .pvw-sum-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .pvw-sum-header {
    padding: 10px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
    border-radius: 6px 6px 0 0;
  }

  .pvw-sum-title {
    font-size: 12px;
    font-weight: 600;
    color: #344054;
  }

  .pvw-sum-body {
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pvw-sum-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pvw-sum-lbl {
    font-size: 12px;
    color: #495057;
  }

  .pvw-sum-val {
    font-size: 13px;
    font-weight: 600;
    color: #101828;
  }

  .pvw-sv-blue {
    color: #0369a1;
  }

  .pvw-sv-green {
    color: #166534;
  }

  .pvw-sv-red {
    color: #dc3545;
  }

  .pvw-sv-amber {
    color: #92400e;
  }
}
</style>
